import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useTranslation } from '../hooks/useTranslation';
import { profileAPI, setAuthToken } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Key, Plus, Copy, Trash2, CheckCircle, ArrowLeft } from 'lucide-react';
import { useToast } from '../components/ui/use-toast';

const APIKeysPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (token) {
      setAuthToken(token);
      loadAPIKeys();
    }
  }, [token]);

  const loadAPIKeys = async () => {
    try {
      const response = await profileAPI.getAPIKeys();
      setApiKeys(response.data.api_keys || []);
    } catch (error) {
      console.error('Failed to load API keys', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!newKeyName.trim()) {
      toast({
        title: t('common.error'),
        description: 'Please enter a key name',
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await profileAPI.createAPIKey({ name: newKeyName });
      setGeneratedKey(response.data.api_key);
      setNewKeyName('');
      loadAPIKeys();
      toast({
        title: t('common.success'),
        description: 'API key generated successfully',
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || 'Failed to generate API key',
        variant: 'destructive',
      });
    }
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast({
      title: t('apiKeys.keyCopied'),
      description: 'API key copied to clipboard',
    });
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm(t('apiKeys.revokeConfirm'))) return;

    try {
      await profileAPI.revokeAPIKey(keyId);
      toast({
        title: t('common.success'),
        description: 'API key revoked successfully',
      });
      loadAPIKeys();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || 'Failed to revoke API key',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6" data-testid="api-keys-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t('apiKeys.title')}</h1>
            <p className="text-slate-600 mt-1">{t('apiKeys.subtitle')}</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white">
              <Plus className="h-4 w-4 mr-2" />
              {t('apiKeys.generate')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('apiKeys.generate')}</DialogTitle>
              <DialogDescription>Create a new API key for LocalMod AI integration</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {generatedKey ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-900 mb-2 flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4" />
                      <span>API Key Generated!</span>
                    </p>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 text-xs bg-white p-2 rounded border border-green-300 break-all">
                        {generatedKey}
                      </code>
                      <Button
                        size="sm"
                        onClick={() => handleCopyKey(generatedKey)}
                        variant="outline"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-red-600 mt-2">Save this key now. You won't be able to see it again!</p>
                  </div>
                  <Button onClick={() => { setGeneratedKey(null); setDialogOpen(false); }}>
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="key_name">{t('apiKeys.name')}</Label>
                    <Input
                      id="key_name"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder={t('apiKeys.namePlaceholder')}
                      className="mt-1"
                    />
                  </div>
                  <DialogFooter>
                    <Button onClick={handleGenerateKey} className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white">
                      {t('apiKeys.generate')}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* API Keys List */}
      <div className="space-y-4">
        {loading ? (
          <p>{t('common.loading')}</p>
        ) : apiKeys.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="text-center py-12">
              <Key className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-600">No API keys yet</p>
              <p className="text-sm text-slate-500 mt-1">Generate your first API key to get started</p>
            </CardContent>
          </Card>
        ) : (
          apiKeys.map((key) => (
            <Card key={key.id} className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Key className="h-5 w-5 text-blue-600" />
                    <div>
                      <CardTitle className="text-lg">{key.name}</CardTitle>
                      <code className="text-xs text-slate-500">{key.key_preview}</code>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleRevokeKey(key.id)}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('apiKeys.revoke')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-4 text-sm text-slate-600">
                  <div>
                    <span className="font-medium">{t('apiKeys.created')}:</span>{' '}
                    {new Date(key.created_at).toLocaleDateString()}
                  </div>
                  {key.last_used && (
                    <div>
                      <span className="font-medium">{t('apiKeys.lastUsed')}:</span>{' '}
                      {new Date(key.last_used).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default APIKeysPage;
